import { $4, $id4 } from '@theme/utilities';

class StickyAtcCustom extends HTMLElement {
  #mainController = null;
  #clickController = null;
  #mql = window.matchMedia('(max-width: 767px)');
  #btnEl = null;
  #actionsEl = null;
  #footerEl = null;
  #isVisible = false;
  #target = null;
  #btnOffsetInSticky = 0;
  #lastScrollY = window.scrollY;
  #lastZone = null;

  connectedCallback() {
    this.#btnEl = $4('[ref="stickyBtn"]', this);
    this.#actionsEl = $4('[ref="stickyActions"]', this);
    this.#footerEl = $id4('hdt-prefooter');

    this.#mainController = new AbortController();
    this.#clickController = new AbortController();
    const { signal } = this.#mainController;

    this.#btnEl?.addEventListener('click', this.#handleClick, { signal: this.#clickController.signal });
    this.#mql.addEventListener('change', this.#handleMQLChange, { signal });
    this.#sourceForm?.addEventListener('product:updateInfo', this.#updateInfo, { signal });
    this.#syncControls();

    if (this.#mql.matches) {
      this.#setupScrollWatch();
    }
  }

  disconnectedCallback() {
    this.#mainController?.abort();
    this.#clickController?.abort();
  }

  get #sourceForm() {
    return document.getElementById(this.dataset.form) ?? document.forms[this.dataset.form] ?? null;
  }

  get #submitButton() {
    return this.#sourceForm?.querySelector('[type="submit"]') ?? null;
  }

  #setupScrollWatch() {
    // Track the actual submit button for pixel-perfect button-to-button alignment
    this.#target = this.#submitButton;
    if (!this.#target) return;

    this.#updateStickyMetrics();

    const { signal } = this.#mainController;
    window.addEventListener('scroll', this.#check, { passive: true, signal });
    window.addEventListener('resize', this.#handleResize, { passive: true, signal });
    this.#check();
  }

  #syncControls() {
    const sourceForm = this.#sourceForm;
    if (!sourceForm || !this.#actionsEl || !this.#btnEl) return;

    const sourceButtons = $4('hdt-buy-buttons', sourceForm);
    const sourceSubmit = $4('.hdt-product-form__submit', sourceForm);
    const sourceWishlist = $4('.hdt-product-form__wishlist', sourceForm);
    const showWishlist = Boolean(sourceWishlist);
    const isUppercase = Boolean(
      sourceButtons?.classList.contains('hdt-text-sm') && sourceButtons?.classList.contains('hdt-uppercase')
    );
    const buttonStyle = sourceSubmit?.classList.contains('hdt-btn-outline') ? 'hdt-btn-outline' : 'hdt-btn-solid';

    this.#actionsEl.classList.toggle('hdt-product-form__buttons', !showWishlist);
    this.#actionsEl.classList.toggle('hdt-product-form__actions', showWishlist);
    this.#actionsEl.classList.toggle('hdt-product-form__actions--wishlist', showWishlist);
    this.#actionsEl.classList.toggle('hdt-text-sm', isUppercase);
    this.#actionsEl.classList.toggle('hdt-uppercase', isUppercase);

    this.#btnEl.classList.remove('hdt-btn-outline', 'hdt-btn-solid');
    this.#btnEl.classList.add(buttonStyle);

    // Keep the sticky markup flat even if the Liquid fallback rendered wishlist markup.
    this.#actionsEl.replaceChildren(this.#btnEl);

    if (showWishlist && sourceWishlist) {
      this.#actionsEl.appendChild(sourceWishlist.cloneNode(true));
    }
  }

  #updateStickyMetrics() {
    if (!this.#btnEl) return;

    // getBoundingClientRect diff stays stable because the sticky bar and its button move together.
    this.#btnOffsetInSticky =
      this.#btnEl.getBoundingClientRect().top - this.getBoundingClientRect().top;
  }

  #getZone(rect, stickyBtnTop) {
    if (rect.bottom <= 0) return 'above';
    if (rect.top <= stickyBtnTop && rect.bottom > 0) return 'handoff';
    return 'below';
  }

  // Zones:
  // 1. below: original ATC is below the sticky handoff point -> snap show
  // 2. handoff: original ATC is visible/in the sticky lane -> snap hide when scrolling down,
  //    slide hide when returning upward from the below-the-fold sticky state
  // 3. above: original ATC has scrolled above the viewport -> slide show, unless footer is in view
  #check = () => {
    if (!this.#mql.matches || !this.#target) return;

    const currentScrollY = window.scrollY;
    const direction =
      currentScrollY > this.#lastScrollY ? 'down' : currentScrollY < this.#lastScrollY ? 'up' : null;
    this.#lastScrollY = currentScrollY;

    const rect = this.#target.getBoundingClientRect();
    const stickyBtnTop = window.innerHeight - this.offsetHeight + this.#btnOffsetInSticky;
    const zone = this.#getZone(rect, stickyBtnTop);
    const previousZone = this.#lastZone;
    const footerInView =
      this.#footerEl ? this.#footerEl.getBoundingClientRect().top <= window.innerHeight : false;

    this.#lastZone = zone;

    if (zone === 'above') {
      if (footerInView) {
        this.#hide('slide');
      } else {
        this.#show('slide');
      }
      return;
    }

    if (zone === 'handoff') {
      const shouldSlideOut = direction === 'up' && previousZone === 'above';
      this.#hide(shouldSlideOut ? 'slide' : 'snap');
      return;
    }

    this.#show('snap');
  };

  #show(mode = 'snap') {
    this.#setMode(mode);
    if (this.#isVisible) return;
    this.#isVisible = true;
    this.dataset.visible = 'true';
    this.removeAttribute('aria-hidden');
  }

  #hide(mode = 'snap') {
    this.#setMode(mode);
    if (!this.#isVisible) return;
    this.#isVisible = false;
    this.dataset.visible = 'false';
    this.setAttribute('aria-hidden', 'true');
  }

  #setMode(mode) {
    if (mode === 'slide') {
      this.dataset.mode = 'slide';
    } else {
      this.removeAttribute('data-mode');
    }
  }

  #handleClick = () => {
    this.#submitButton?.click();
  };

  #handleResize = () => {
    this.#updateStickyMetrics();
    this.#check();
  };

  #handleMQLChange = (e) => {
    if (e.matches) {
      this.#lastScrollY = window.scrollY;
      this.#syncControls();
      this.#setupScrollWatch();
    } else {
      this.#lastZone = null;
      this.#hide('snap');
    }
  };

  #updateInfo = (event) => {
    const { sectionId, html } = event.detail;
    const fragment = document.createRange().createContextualFragment(html);
    const sectionEl = $4(`#shopify-section-${sectionId}`, fragment) ?? fragment;
    const newSticky = $4('hdt-sticky-atc-custom', sectionEl);
    if (!newSticky) return;

    const newBtn = $4('[ref="stickyBtn"]', newSticky);

    if (newBtn) {
      this.#btnEl.replaceWith(newBtn);
      this.#btnEl = newBtn;
    }

    this.#clickController.abort();
    this.#clickController = new AbortController();
    this.#btnEl?.addEventListener('click', this.#handleClick, { signal: this.#clickController.signal });

    this.#syncControls();

    // Re-cache offset in case the sticky button layout changed.
    this.#updateStickyMetrics();
    // Re-target the new submit button
    this.#target = this.#submitButton;
    this.#check();
  };
}

customElements.define('hdt-sticky-atc-custom', StickyAtcCustom);
